import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../components/ui/PageHero';
import FadeUp from '../components/ui/FadeUp';
import { BUSINESS, YEARS, MAKE_MODELS } from '../data/siteData';

export default function RequestPartPage() {
  const [form, setForm] = useState({
    name: '', phone: '', email: '',
    year: '', make: '', model: '',
    part: '', condition: 'Any', notes: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const makes = Object.keys(MAKE_MODELS).sort();
  const models = form.make && MAKE_MODELS[form.make] ? MAKE_MODELS[form.make] : [];

  const set = (field) => (e) => {
    setForm(f => ({ ...f, [field]: e.target.value }));
    if (field === 'make') setForm(f => ({ ...f, make: e.target.value, model: '' }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Required';
    if (!form.phone.trim()) errs.phone = 'Required';
    if (!form.part.trim()) errs.part = 'Required';
    return errs;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSubmitting(true);
    setTimeout(() => { setSubmitting(false); setSubmitted(true); }, 1000);
  };

  const inputStyle = (field) => ({
    background: 'var(--ice)',
    border: `1.5px solid ${errors[field] ? '#e05252' : 'var(--silver-light)'}`,
    color: 'var(--text-dark)',
    fontFamily: 'Barlow, sans-serif', fontSize: '0.95rem',
    padding: '0.8rem 1rem', borderRadius: 8,
    width: '100%', outline: 'none', appearance: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
  });

  const focusIn = (e) => { e.target.style.borderColor = 'var(--navy)'; e.target.style.boxShadow = '0 0 0 3px rgba(13,27,42,0.08)'; };
  const focusOut = (field) => (e) => { e.target.style.borderColor = errors[field] ? '#e05252' : 'var(--silver-light)'; e.target.style.boxShadow = 'none'; };

  return (
    <>
      <PageHero
        tag="Parts Locator"
        title="Request a Part"
        subtitle="Can't find the part you need? Fill out the form and we'll search our network across Ontario to locate it for you."
      />

      <section style={{ padding: '5rem 2rem', background: 'var(--ice)' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>

          {/* How It Works */}
          <FadeUp>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1.25rem', marginBottom: '3rem' }}>
              {[
                { step: '1', icon: '📋', title: 'Fill the Form', desc: 'Tell us your vehicle and the part you need.' },
                { step: '2', icon: '🔎', title: 'We Search', desc: 'We check our yard and our province-wide network.' },
                { step: '3', icon: '📞', title: 'We Call You', desc: 'We\'ll contact you with availability and pricing.' },
                { step: '4', icon: '🚗', title: 'Pick It Up', desc: 'Come in or we can arrange delivery options.' },
              ].map(s => (
                <div key={s.step} style={{ background: 'white', borderRadius: 12, padding: '1.5rem', textAlign: 'center', boxShadow: '0 2px 16px rgba(13,27,42,0.06)' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>{s.icon}</div>
                  <div style={{ fontFamily: '"Bebas Neue"', fontSize: '1.2rem', color: 'var(--orange)', marginBottom: '0.25rem' }}>Step {s.step}</div>
                  <h4 style={{ fontFamily: '"Barlow Condensed"', fontWeight: 700, fontSize: '1rem', color: 'var(--navy)', marginBottom: '0.25rem' }}>{s.title}</h4>
                  <p style={{ fontSize: '0.82rem', color: 'var(--text-mid)', lineHeight: 1.5 }}>{s.desc}</p>
                </div>
              ))}
            </div>
          </FadeUp>

          {/* Form Card */}
          <FadeUp delay={100}>
            <div style={{ background: 'white', borderRadius: 16, padding: '2.5rem', boxShadow: '0 4px 40px rgba(13,27,42,0.08)' }}>
              {submitted ? (
                <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                  <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>✅</div>
                  <h2 style={{ fontFamily: '"Bebas Neue"', fontSize: '2.5rem', color: 'var(--navy)', letterSpacing: '0.03em', marginBottom: '0.75rem' }}>Request Submitted!</h2>
                  <p style={{ color: 'var(--text-mid)', fontSize: '1.05rem', lineHeight: 1.7, maxWidth: 480, margin: '0 auto 2rem' }}>
                    Thanks {form.name}! We've received your part request for <strong>{form.part}</strong>
                    {form.year || form.make ? ` (${[form.year, form.make, form.model].filter(Boolean).join(' ')})` : ''}. 
                    We'll be in touch within 1 business day.
                  </p>
                  <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                    <a href={`tel:${BUSINESS.phoneRaw}`} className="btn-primary">📞 Call for Faster Service</a>
                    <Link to="/inventory" className="btn-dark">🔍 Browse Inventory</Link>
                  </div>
                </div>
              ) : (
                <>
                  <h2 style={{ fontFamily: '"Bebas Neue"', fontSize: '1.8rem', color: 'var(--navy)', letterSpacing: '0.03em', marginBottom: '0.5rem' }}>
                    Part Request Form
                  </h2>
                  <p style={{ fontSize: '0.9rem', color: 'var(--text-mid)', marginBottom: '2rem' }}>
                    Fields marked * are required. We typically respond within 1 business day.
                  </p>

                  <form onSubmit={handleSubmit} noValidate>
                    {/* Contact Info */}
                    <div style={{ marginBottom: '2rem' }}>
                      <h3 style={{ fontFamily: '"Barlow Condensed"', fontSize: '0.85rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-light)', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '1px solid var(--silver-light)' }}>
                        Your Contact Info
                      </h3>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                        <div>
                          <label className="form-label">Full Name *</label>
                          <input value={form.name} onChange={set('name')} placeholder="John Smith" style={inputStyle('name')} onFocus={focusIn} onBlur={focusOut('name')} />
                          {errors.name && <p style={{ color: '#e05252', fontSize: '0.75rem', marginTop: '0.2rem' }}>{errors.name}</p>}
                        </div>
                        <div>
                          <label className="form-label">Phone *</label>
                          <input value={form.phone} onChange={set('phone')} placeholder="(519) 000-0000" type="tel" style={inputStyle('phone')} onFocus={focusIn} onBlur={focusOut('phone')} />
                          {errors.phone && <p style={{ color: '#e05252', fontSize: '0.75rem', marginTop: '0.2rem' }}>{errors.phone}</p>}
                        </div>
                        <div>
                          <label className="form-label">Email</label>
                          <input value={form.email} onChange={set('email')} placeholder="you@email.com" type="email" style={inputStyle('email')} onFocus={focusIn} onBlur={focusOut('email')} />
                        </div>
                      </div>
                    </div>

                    {/* Vehicle Info */}
                    <div style={{ marginBottom: '2rem' }}>
                      <h3 style={{ fontFamily: '"Barlow Condensed"', fontSize: '0.85rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-light)', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '1px solid var(--silver-light)' }}>
                        Vehicle Information
                      </h3>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
                        <div>
                          <label className="form-label">Year</label>
                          <select value={form.year} onChange={set('year')} style={inputStyle('year')} onFocus={focusIn} onBlur={focusOut('year')}>
                            <option value="">Select Year</option>
                            {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="form-label">Make</label>
                          <select value={form.make} onChange={e => { setForm(f => ({ ...f, make: e.target.value, model: '' })); }} style={inputStyle('make')} onFocus={focusIn} onBlur={focusOut('make')}>
                            <option value="">Select Make</option>
                            {makes.map(m => <option key={m} value={m}>{m}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="form-label">Model</label>
                          <select value={form.model} onChange={set('model')} style={inputStyle('model')} onFocus={focusIn} onBlur={focusOut('model')}>
                            <option value="">Select Model</option>
                            {models.map(m => <option key={m} value={m}>{m}</option>)}
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Part Info */}
                    <div style={{ marginBottom: '2rem' }}>
                      <h3 style={{ fontFamily: '"Barlow Condensed"', fontSize: '0.85rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-light)', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '1px solid var(--silver-light)' }}>
                        Part Details
                      </h3>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }} className="part-grid">
                        <div>
                          <label className="form-label">Part Name *</label>
                          <input value={form.part} onChange={set('part')} placeholder="e.g. Front passenger door, alternator..." style={inputStyle('part')} onFocus={focusIn} onBlur={focusOut('part')} />
                          {errors.part && <p style={{ color: '#e05252', fontSize: '0.75rem', marginTop: '0.2rem' }}>{errors.part}</p>}
                        </div>
                        <div>
                          <label className="form-label">Condition Preference</label>
                          <select value={form.condition} onChange={set('condition')} style={inputStyle('condition')} onFocus={focusIn} onBlur={focusOut('condition')}>
                            <option>Any</option>
                            <option>Excellent Only</option>
                            <option>Good or Better</option>
                            <option>Any Working Condition</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="form-label">Additional Notes</label>
                        <textarea value={form.notes} onChange={set('notes')} rows={4} placeholder="e.g. Colour preference, OEM only, part number, urgency..." style={{ ...inputStyle('notes'), resize: 'vertical', minHeight: 100 }} onFocus={focusIn} onBlur={focusOut('notes')} />
                      </div>
                    </div>

                    <button type="submit" className="btn-primary" style={{ width: '100%', justifyContent: 'center', fontSize: '1.05rem', padding: '1rem', opacity: submitting ? 0.7 : 1 }} disabled={submitting}>
                      {submitting ? '⏳ Submitting Request...' : '📋 Submit Part Request →'}
                    </button>
                  </form>
                </>
              )}
            </div>
          </FadeUp>

          {/* Or call */}
          <FadeUp delay={150}>
            <div style={{ textAlign: 'center', marginTop: '2rem', padding: '1.5rem', background: 'var(--navy)', borderRadius: 12 }}>
              <p style={{ color: 'var(--silver)', fontSize: '1rem' }}>
                Prefer to talk? Call us directly:{' '}
                <a href={`tel:${BUSINESS.phoneRaw}`} style={{ color: 'var(--orange)', textDecoration: 'none', fontWeight: 700, fontSize: '1.1rem' }}>
                  {BUSINESS.phone}
                </a>
              </p>
            </div>
          </FadeUp>
        </div>
      </section>
      <style>{`@media (max-width: 600px) { .part-grid { grid-template-columns: 1fr !important; } }`}</style>
    </>
  );
}
